import React, { useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { fmtDate } from "@/lib/format";
import { Pencil, Trash2, Check, X, Plus, Inbox } from "lucide-react";

const n2 = (v) => (Number(v) || 0).toFixed(2);
const today = () => new Date().toISOString().slice(0, 10);
const TH = "py-3 px-3 text-left font-medium text-[13px] whitespace-nowrap border-r border-white/20 last:border-r-0";
const TD = "py-2.5 px-3 border-r border-slate-200 last:border-r-0 align-middle";
const INP = "h-8 rounded-md border border-slate-300 bg-white px-2 text-[13px] text-slate-800 focus:outline-none focus:ring-2 focus:ring-sky-500";
const errMsg = (e, d) => { const x = e?.response?.data?.detail; return typeof x === "string" ? x : d; };

function EditRow({ init, onSave, onCancel, testid }) {
  const [date, setDate] = useState(init.payment_date);
  const [amt, setAmt] = useState(String(init.amount ?? ""));
  return (
    <>
      <td className={TD}><input type="date" value={date} onChange={(e) => setDate(e.target.value)} className={INP} data-testid={`${testid}-date`} /></td>
      <td className={TD}><input type="number" min="0" step="0.01" value={amt} onChange={(e) => setAmt(e.target.value)} className={`${INP} w-32 text-right`} data-testid={`${testid}-amount`} /></td>
      <td className={`${TD} no-print whitespace-nowrap`}>
        <button onClick={() => onSave(date, Number(amt))} className="p-1.5 text-emerald-600 hover:bg-emerald-50 rounded" data-testid={`${testid}-save`}><Check className="h-4 w-4" /></button>
        {onCancel && <button onClick={onCancel} className="p-1.5 text-slate-500 hover:bg-slate-100 rounded" data-testid={`${testid}-cancel`}><X className="h-4 w-4" /></button>}
      </td>
    </>
  );
}

export function PaymentRegister({ owner, hubId, rows, onChanged }) {
  const [editing, setEditing] = useState(null);
  const [adding, setAdding] = useState(false);
  const canEdit = hubId && hubId !== "all";
  const save = async (id, date, amount) => {
    if (!date || !(amount > 0)) return toast.error("Enter a date and an amount above 0");
    try {
      const body = { owner_name: owner, hub_id: hubId, payment_date: date, amount };
      if (id) await api.put(`/owner-payments/${id}`, body); else await api.post("/owner-payments", body);
      toast.success(id ? "Payment updated" : "Payment added");
      setEditing(null); setAdding(false); onChanged();
    } catch (e) { toast.error(errMsg(e, "Could not save payment")); }
  };
  const del = async (p) => {
    if (!window.confirm(`Delete payment of ₹${n2(p.amount)} on ${fmtDate(p.payment_date)}?`)) return;
    try { await api.delete(`/owner-payments/${p.id}`); toast.success("Payment deleted"); onChanged(); } catch (e) { toast.error(errMsg(e, "Could not delete payment")); }
  };
  const total = rows.reduce((a, p) => a + (p.amount || 0), 0);
  return (
    <div data-testid="os-payments">
      <div className="flex items-center justify-between mb-2.5 mt-1">
        <h3 className="text-sm font-semibold text-slate-800">Payment Register</h3>
        {canEdit ? <button onClick={() => setAdding(true)} className="no-print inline-flex items-center gap-1 text-xs font-medium text-sky-700 hover:underline" data-testid="os-payment-add"><Plus className="h-3.5 w-3.5" />Add payment</button>
          : <span className="no-print text-xs text-slate-500" data-testid="os-payment-hub-hint">Pick a hub to add payments</span>}
      </div>
      <div className="rounded-lg border border-slate-200 overflow-hidden">
        <table className="w-full text-[13px] text-slate-700">
          <thead className="bg-slate-600 text-white"><tr>{["Sl No", "Date", "Amount"].map((h) => <th key={h} className={TH}>{h}</th>)}<th className={`${TH} no-print w-20`}></th></tr></thead>
          <tbody>
            {rows.map((p, i) => (
              <tr key={p.id} className="border-t border-slate-200" data-testid={`os-payment-row-${i}`}>
                <td className={TD}>{i + 1}</td>
                {editing === p.id ? <EditRow init={p} testid={`os-payment-edit-${i}`} onSave={(d, a) => save(p.id, d, a)} onCancel={() => setEditing(null)} /> : <>
                  <td className={`${TD} whitespace-nowrap`}>{fmtDate(p.payment_date)}</td>
                  <td className={`${TD} text-right`} data-testid={`os-payment-amount-${i}`}>{n2(p.amount)}</td>
                  <td className={`${TD} no-print whitespace-nowrap`}>
                    {canEdit && <><button onClick={() => setEditing(p.id)} className="p-1.5 text-slate-500 hover:bg-slate-100 rounded" data-testid={`os-payment-edit-btn-${i}`}><Pencil className="h-3.5 w-3.5" /></button>
                      <button onClick={() => del(p)} className="p-1.5 text-rose-500 hover:bg-rose-50 rounded" data-testid={`os-payment-delete-${i}`}><Trash2 className="h-3.5 w-3.5" /></button></>}
                  </td>
                </>}
              </tr>
            ))}
            {adding && <tr className="border-t border-slate-200 bg-sky-50/50 no-print"><td className={TD}>{rows.length + 1}</td><EditRow init={{ payment_date: today(), amount: "" }} testid="os-payment-new" onSave={(d, a) => save(null, d, a)} onCancel={() => setAdding(false)} /></tr>}
            {rows.length === 0 && !adding && <tr><td colSpan={4}><div className="py-8 flex flex-col items-center gap-1.5 text-slate-400"><Inbox className="h-8 w-8 stroke-1" /><span className="text-xs">No payments in this period</span></div></td></tr>}
          </tbody>
          {rows.length > 0 && <tfoot className="border-t border-slate-200 font-medium"><tr><td colSpan={2} className="py-3 px-3 text-right">Total Paid</td><td className="py-3 px-3 text-right" data-testid="os-payments-total">{n2(total)}</td><td className="no-print"></td></tr></tfoot>}
        </table>
      </div>
    </div>
  );
}
